import { HD, PrivateKey, Utils } from "@bsv/sdk";
import type {
	BapAccountBackup,
	BapMasterBackup,
	WifBackup,
} from "bitcoin-backup";

export type ValidatedLegacyBackup =
	| BapMasterBackup
	| BapAccountBackup
	| WifBackup;

function isWif(value: unknown): value is string {
	if (typeof value !== "string" || !value) {
		return false;
	}
	try {
		const decoded = Utils.fromBase58Check(value);
		if (decoded.prefix[0] !== 128 && decoded.prefix[0] !== 239) {
			return false;
		}
		const key = PrivateKey.fromWif(value);
		// SDK parsing reduces out-of-range scalars; never silently accept that change.
		return (
			key.gtn(0) &&
			key.toArray("be", 32).every((byte, i) => byte === decoded.data[i])
		);
	} catch {
		return false;
	}
}

function isXprv(value: unknown): value is string {
	if (typeof value !== "string" || !value) {
		return false;
	}
	try {
		const hd = HD.fromString(value);
		return !!hd.privKey && hd.privKey.gtn(0) && hd.toString() === value;
	} catch {
		return false;
	}
}

/** Validate legacy key sources without deriving or rewriting any identity. */
export function validateLegacyBackup(
	data: unknown
): data is ValidatedLegacyBackup {
	if (!data || typeof data !== "object" || Array.isArray(data)) {
		return false;
	}
	const backup = data as Record<string, unknown>;
	// Seed dispatch is deliberately separate. Never treat a marked envelope as legacy.
	if (
		[
			"format",
			"scheme",
			"entropyBytes",
			"passphrasePolicy",
			"profiles",
			"nextProfileIndex",
		].some((key) => key in backup)
	) {
		return false;
	}
	if (
		["ordPk", "payPk", "identityPk", "encryptedVault", "chromeStorage"].some(
			(key) => key in backup
		)
	) {
		return false;
	}
	if (["rootPk", "xprv", "wif"].filter((key) => key in backup).length !== 1) {
		return false;
	}
	if ("rootPk" in backup) {
		return (
			isWif(backup.rootPk) &&
			typeof backup.ids === "string" &&
			!("id" in backup) &&
			(!("mnemonic" in backup) || typeof backup.mnemonic === "string")
		);
	}
	if ("xprv" in backup) {
		// Historical mnemonic passphrase/path policy is unknown: xprv remains authoritative.
		return (
			isXprv(backup.xprv) &&
			typeof backup.ids === "string" &&
			typeof backup.mnemonic === "string" &&
			!("id" in backup)
		);
	}
	return (
		isWif(backup.wif) &&
		!("ids" in backup) &&
		!("mnemonic" in backup) &&
		(!("id" in backup) || (typeof backup.id === "string" && !!backup.id.trim()))
	);
}

export function assertValidLegacyBackup(
	data: unknown
): asserts data is ValidatedLegacyBackup {
	if (!validateLegacyBackup(data)) {
		throw new Error("Invalid or unsupported backup structure");
	}
}
